import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';

// Inactivity auto-logout — shared by every role (admin panel + all dashboards).
// 9 min of no activity → warning modal; 10 min → Firebase signOut + onLogout()
// (clears app state / returns to login) + a localStorage flag the login page
// surfaces as "logged out due to inactivity". Timers live in refs so the
// high-frequency activity events (mousemove/scroll) never trigger re-renders.
const INACTIVITY_WARNING_MS = 9 * 60 * 1000;   // 9 min → warning modal
const INACTIVITY_LOGOUT_MS  = 10 * 60 * 1000;  // 10 min → sign out

export default function InactivityLogout({ onLogout }) {
  const [warning, setWarning] = useState(false);
  const warnTimerRef   = useRef(null);
  const logoutTimerRef = useRef(null);
  const warnShownRef   = useRef(false);

  const clearTimers = () => {
    if (warnTimerRef.current)   { clearTimeout(warnTimerRef.current);   warnTimerRef.current = null; }
    if (logoutTimerRef.current) { clearTimeout(logoutTimerRef.current); logoutTimerRef.current = null; }
  };

  const doLogout = async () => {
    clearTimers();
    try { localStorage.setItem('logoutReason', 'inactivity'); } catch (_) {}
    try { await signOut(auth); } catch (_) {}
    if (typeof onLogout === 'function') onLogout();
  };

  const armTimers = () => {
    clearTimers();
    warnTimerRef.current = setTimeout(() => {
      warnShownRef.current = true;
      setWarning(true);
    }, INACTIVITY_WARNING_MS);
    logoutTimerRef.current = setTimeout(() => { doLogout(); }, INACTIVITY_LOGOUT_MS);
  };

  const resetTimer = () => {
    // Only touch state when the warning is up, so ordinary mousemoves reset the
    // timers (via refs) without re-rendering.
    if (warnShownRef.current) {
      warnShownRef.current = false;
      setWarning(false);
    }
    armTimers();
  };

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    const onActivity = () => resetTimer();
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    armTimers();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!warning) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="inactivity-warning-title"
      style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' }}>
        <h3 id="inactivity-warning-title" style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, color: '#111' }}>Still there?</h3>
        <p style={{ margin: '0 0 20px', fontSize: 14, color: '#4b5563', lineHeight: 1.5 }}>
          You have been inactive for 9 minutes. You will be logged out in 1 minute unless you continue.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button
            onClick={resetTimer}
            style={{ background: '#00a651', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >Stay Logged In</button>
          <button
            onClick={doLogout}
            style={{ background: '#fff', color: '#111', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >Log Out Now</button>
        </div>
      </div>
    </div>
  );
}
